import { useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Home, Sun, ClipboardList, User, Plus, LogOut, Archive, ShoppingCart } from "lucide-react";
import NotificationBell from "../NotificationBell";

const TABS = [
  { to: "/app", icon: Home, label: "Home", end: true },
  { to: "/app/installations", icon: Sun, label: "Installs" },
  { to: "/app/tickets", icon: ClipboardList, label: "Requests" },
  { to: "/app/orders", icon: Archive, label: "Orders" },
  { to: "/app/profile", icon: User, label: "Profile" },
];

export default function CustomerAppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const onOrdersTab = location.pathname.startsWith("/app/orders");

  useEffect(() => {
    api.get("/customer-app/onboarding/status").then(({ data }) => {
      if (data.needs_onboarding) nav("/app/onboarding", { replace: true });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <div className="max-w-md mx-auto min-h-screen relative bg-[#FAF8F5] pb-28">
        {/* Top bar */}
        <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/80">
          <div className="px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/purvodaya-logo.png" alt="Purvodaya Solar" className="w-9 h-9 object-contain flex-shrink-0" draggable={false} />
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97706]">Purvodaya Solar</div>
                <div className="text-[14px] font-bold leading-tight truncate text-[#0F172A]">
                  Hi, {user?.name?.split(" ")[0] || "there"} 👋
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <NotificationBell />
              <button
                onClick={async () => { await logout(); nav("/app/login"); }}
                className="p-2 rounded-full text-slate-400 hover:text-[#0D4721] hover:bg-emerald-50"
                data-testid="customer-logout"
              >
                <LogOut className="w-4.5 h-4.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <Outlet />
        </div>

        {/* Floating action button — context-aware: New Order on the Orders tab, Raise a Complaint everywhere else */}
        {onOrdersTab ? (
          <NavLink
            to="/app/buy-installation"
            data-testid="fab-new-order"
            className="fixed bottom-24 right-4 z-40 bg-gradient-to-r from-[#0D4721] to-[#15803D] text-white shadow-[0_4px_14px_0_rgba(13,71,33,0.35)] rounded-full p-4 hover:scale-110 active:scale-95 transition-transform"
            style={{ maxWidth: "calc(28rem - 1rem)" }}
          >
            <ShoppingCart className="w-6 h-6" strokeWidth={2.5} />
          </NavLink>
        ) : (
          <NavLink
            to="/app/raise-complaint"
            data-testid="fab-raise-complaint"
            className="fixed bottom-24 right-4 z-40 bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-[0_4px_14px_0_rgba(245,158,11,0.35)] rounded-full p-4 hover:scale-110 active:scale-95 transition-transform"
            style={{ maxWidth: "calc(28rem - 1rem)" }}
          >
            <Plus className="w-6 h-6" strokeWidth={2.5} />
          </NavLink>
        )}

        {/* Bottom Tab Bar */}
        <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-2">
          <div className="max-w-md mx-auto bg-white/90 backdrop-blur-lg border border-slate-200/80 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] flex justify-around items-center rounded-2xl py-1">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`bottom-nav-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "flex flex-col items-center gap-1 py-1.5 px-2 my-1 text-[10px] font-semibold transition-all duration-200 rounded-xl " +
                  (isActive ? "text-[#0D4721] bg-emerald-100/70 scale-105" : "text-slate-500 hover:text-slate-800")
                }
              >
                <t.icon className="w-4.5 h-4.5" strokeWidth={2} />
                {t.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
